import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { X, Minus, Plus, Calendar, MapPin, ShoppingCart, ShieldCheck, ArrowRight, Lock, Check, LifeBuoy } from 'lucide-react';
import { toast } from 'react-toastify';
import { computeCartFees, feeConfigFromCountryRow } from '../../utils/fees';
import { getCountryList } from '../../stores/actions/eventsActions';
import { startCheckoutSession, syncCheckoutContact } from '../../fraud';
import { openModal } from '../../stores/slices/commonSlice';
import { setSelectedTicket } from '../../stores/slices/eventSlice';
import useModalHeaderPadding from '../../hooks/useModalHeaderPadding';
import { scheduleDocumentScrollLockRelease } from '../../utils/scrollLock';
import Payment, { RESERVATION_SECONDS, formatTimer } from './Payment';

/**
 * TicketSelectModal — the new two-step purchase entry point that replaces the
 * legacy TicketBooking2 + RegisterTickets chain.
 *
 * Step 1 (this modal): pick ticket quantities with a live order summary.
 * Step 2 (Checkout): contact, holder names, coupon, and payment in the same modal.
 */
const MAX_PER_ORDER = 10;

export default function TicketSelectModal({ open, onClose, onContinue }) {
    const dispatch = useDispatch();

    const eventDetails = useSelector((state) => state.event.eventDetails);
    const ticketList = useSelector((state) => state.event.presold);
    const country = useSelector((state) => state.event.country);
    const getCountryFeeListDetails = useSelector((state) => state.event.getCountryFeeListDetails);
    const user = useSelector((state) => state.auth.user);
    const loginModalOpen = useSelector((state) => state.common.openModal);

    // Card processor badge — resolved server-side per event and delivered on
    // eventDetails.payment_gateway ('stripe' | 'powertranz'). Defaults to
    // Stripe for older API responses that predate the field.
    const gatewayBadge = (extra = '') => (
        <img
            src={eventDetails?.payment_gateway === 'powertranz'
                ? '/assets/images/powertranz.jpg'
                : '/assets/images/stripe.jpg'}
            alt={eventDetails?.payment_gateway === 'powertranz' ? 'PowerTranz' : 'Stripe'}
            className={`h-4 w-auto object-contain ${extra}`}
        />
    );


    const [counts, setCounts] = useState([]);
    const [feeConfig, setFeeConfig] = useState(null);
    const [currency, setCurrency] = useState('');
    const [step, setStep] = useState(1);
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [showHolderNames, setShowHolderNames] = useState(false);
    const [holderDetails, setHolderDetails] = useState([]);
    const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
    // Fraud gate at ticket selection: when the server flags this buyer/device as
    // a hard block, we surface a restriction screen with a Contact Support
    // option instead of letting them reach the payment step.
    const [checkoutBlocked, setCheckoutBlocked] = useState(false);
    // Mirrored for the Escape handler, for the same reason as
    // reservationExpiredRef: that effect is keyed on [open] only, so it cannot
    // read either of these — or call the current closeNow — through a closure.
    const checkoutBlockedRef = useRef(false);
    const closeNowRef = useRef(null);
    const [blockMessage, setBlockMessage] = useState('');
    const [checkingBlock, setCheckingBlock] = useState(false);
    // Live Pay Now state mirrored up from the embedded Payment/CheckoutForm so
    // the summary sidebar's Pay Now button reflects the "Verifying…"/processing
    // loading state on the first click instead of looking unresponsive.
    const [payState, setPayState] = useState({ button: '', disabled: false });
    const [isMobileViewport, setIsMobileViewport] = useState(false);
    // DOM nodes the coupon widget (owned by <Payment>) is portalled into.
    // Mobile and desktop use separate slots so the coupon scrolls with the
    // form on mobile but stays in the sidebar on desktop.
    const [couponSlot, setCouponSlot] = useState(null);
    const mobileCouponSlotRef = useRef(null);
    const desktopCouponSlotRef = useRef(null);
    // The reservation countdown. It lives HERE, not in <Payment>, because the
    // hold covers the whole checkout: the buyer is sitting on those tickets from
    // the moment they pick them, not from the moment they reach the card form.
    // <Payment> only exists at step 2, so a clock it owned could neither show
    // during ticket selection nor carry a part-spent countdown into payment — it
    // restarted at 10:00 on arrival. The text is written straight into a ref'd
    // node so ticking never re-renders this tree (and never drops focus in the
    // card fields below).
    const timerTextRef = useRef(null);
    // Reservation ran out. <Payment> raises this and puts up its own expiry
    // dialog; this modal takes its chrome down so that dialog is the only thing
    // on screen. Hidden rather than unmounted on purpose — <Payment> is a child
    // here, so closing for real would destroy the dialog it just opened along
    // with the refund call it may still be making. The dialog is portalled to
    // <body>, so `invisible` on this tree doesn't touch it.
    const [reservationExpired, setReservationExpired] = useState(false);
    // Mirrored for the Escape handler, whose effect is keyed on [open] only —
    // re-running it to pick up a state change would redo the body scroll-lock
    // save/restore and can leave the page stuck unscrollable.
    const reservationExpiredRef = useRef(false);
    useEffect(() => {
        reservationExpiredRef.current = reservationExpired;
    }, [reservationExpired]);
    useEffect(() => {
        checkoutBlockedRef.current = checkoutBlocked;
    }, [checkoutBlocked]);

    const syncCouponSlot = () => {
        if (step !== 2) {
            setCouponSlot(null);
            return;
        }
        const isDesktop = window.matchMedia('(min-width: 768px)').matches;
        setCouponSlot(isDesktop ? desktopCouponSlotRef.current : mobileCouponSlotRef.current);
    };

    const attachMobileCouponSlot = (el) => {
        mobileCouponSlotRef.current = el;
        syncCouponSlot();
    };

    const attachDesktopCouponSlot = (el) => {
        desktopCouponSlotRef.current = el;
        syncCouponSlot();
    };

    useEffect(() => {
        syncCouponSlot();
        const mq = window.matchMedia('(min-width: 768px)');
        mq.addEventListener('change', syncCouponSlot);
        return () => mq.removeEventListener('change', syncCouponSlot);
    }, [step, open]);

    const SUPPORT_EMAIL = 'support@apsession.com';

    // Guests are real sessions too: guestLoginAction stores {id: 0}, so the
    // check must be "has an id" (0 included), not truthiness — `user.id` alone
    // would bounce guests back to the login modal forever.
    const hasAuthSession = Boolean(user && typeof user === 'object' && user.id != null);
    // Guests carry the sentinel id 0 (guestLoginAction). Phone is collected from
    // them at checkout since it's not on any account.
    const isGuest = !hasAuthSession || user.id === 0 || user.id === '0';
    const loginWasOpen = useRef(false);
    // Prevents a second checkout from firing before `checkingBlock` re-renders
    // the button disabled (e.g. a rapid double-click, or the login-close effect
    // racing goCheckout).
    const checkoutInFlight = useRef(false);
    const { paddingStyle: modalHeaderPaddingStyle } = useModalHeaderPadding();

    useEffect(() => {
        const mq = window.matchMedia('(max-width: 767px)');
        const syncMobileViewport = () => setIsMobileViewport(mq.matches);

        syncMobileViewport();
        mq.addEventListener('change', syncMobileViewport);
        return () => mq.removeEventListener('change', syncMobileViewport);
    }, []);

    // Reset step when the modal closes; reset counts when tickets reload.
    useEffect(() => {
        if (!open) {
            setStep(1);
            setShowLeaveConfirm(false);
            setCheckoutBlocked(false);
            setBlockMessage('');
            setCheckingBlock(false);
            setReservationExpired(false);
            setPayState({ button: '', disabled: false });
            // Re-arm the one-shot seeding for the next open. `email`/`phone`
            // themselves are deliberately left alone, so a typed address is
            // still there when the buyer comes back; the account value only
            // fills in again if they left the field empty.
            prefilledEmailRef.current = false;
            prefilledPhoneRef.current = false;
            return;
        }
        setCounts(Array(ticketList?.length || 0).fill(0));
    }, [open, ticketList]);

    // payState is mirrored up from <Payment>, which only exists at step 2 — so
    // once it stops rendering, whatever it last reported is frozen. Expiry sets
    // disabled:true to lock the Pay button, and checkoutBusy is derived from
    // that, so a stale true left the "Pay Now … / Please don't close or refresh
    // this page." curtain covering ticket selection on the next open. Any return
    // to step 1 means nothing is in flight to report.
    useEffect(() => {
        if (step === 1) setPayState({ button: '', disabled: false });
    }, [step]);

    // `disabled` from <Payment> means a charge or a 3DS/OTP challenge is in
    // flight. Held in a ref so the countdown can read it without listing it as a
    // dependency — that would restart the interval, resetting the clock to a
    // full 10:00 on every pay attempt.
    const payInFlightRef = useRef(false);
    useEffect(() => {
        payInFlightRef.current = payState.disabled;
    }, [payState.disabled]);
    // Which step the clock lands on decides what expiry means, and the countdown
    // effect cannot read state through a closure — see the [open] keying below.
    const stepRef = useRef(step);
    useEffect(() => {
        stepRef.current = step;
    }, [step]);

    // One countdown for the whole checkout, started when the modal opens and
    // carried across both steps. Keyed on [open] alone so moving between ticket
    // selection and payment neither restarts nor stops it.
    useEffect(() => {
        if (!open) {
            setReservationExpired(false);
            if (timerTextRef.current) {
                timerTextRef.current.textContent = formatTimer(RESERVATION_SECONDS);
            }
            return undefined;
        }

        setReservationExpired(false);
        let remaining = RESERVATION_SECONDS;
        const paint = () => {
            if (timerTextRef.current) {
                timerTextRef.current.textContent = formatTimer(remaining);
            }
        };
        paint();

        const id = setInterval(() => {
            // Hold the clock while a payment is in flight. Expiring mid-payment
            // tore down a live authentication and then refunded a charge that
            // was about to succeed — at that point the buyer is committed, not
            // sitting on a reservation they might abandon. It resumes if the
            // attempt fails and they retry.
            if (payInFlightRef.current) return;

            remaining -= 1;
            paint();
            if (remaining <= 0) {
                clearInterval(id);
                if (stepRef.current === 2) {
                    // <Payment> follows this via its `expired` prop: it puts up
                    // the expiry dialog and refunds an in-flight charge if there
                    // is one.
                    setReservationExpired(true);
                    return;
                }
                // Step 1: <Payment> is not mounted, so nothing would render that
                // dialog — and setting reservationExpired here would only hide
                // this modal's chrome, leaving an invisible sheet over the page.
                // Nothing has been charged yet either, so simply say so and let
                // the buyer start again.
                toast.info('Your ticket reservation expired. Please select your tickets again.');
                onClose?.();
            }
        }, 1000);

        return () => clearInterval(id);
    }, [open]);

    // Resolve the fee config + currency for the order summary's combined Fees
    // line. Prefer the event's server-resolved `fee_settings` — it honors
    // custom_pricing_id (so what we display matches what PaymentController
    // charges). Only fall back to the buyer's selected-country row for events
    // without custom pricing (or older API responses lacking the field).
    useEffect(() => {
        const eventFee = eventDetails?.fee_settings;
        if (eventFee) {
            setFeeConfig(feeConfigFromCountryRow(eventFee));
            setCurrency(eventFee.currency);
            return;
        }
        if (!getCountryFeeListDetails) {
            dispatch(getCountryList());
            return;
        }
        const row = getCountryFeeListDetails.find((d) => d.country === country);
        if (!row) return;
        setFeeConfig(feeConfigFromCountryRow(row));
        setCurrency(row.currency);
    }, [eventDetails, getCountryFeeListDetails, country, dispatch]);

    // Lock body scroll + Escape while open.
    useEffect(() => {
        if (!open) return undefined;
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        const onKey = (e) => {
            if (e.key !== 'Escape') return;
            // Expired: this modal is hidden and the expiry dialog owns the
            // screen, so Escape must not raise a "Leave checkout?" prompt into
            // a tree nobody can see.
            if (reservationExpiredRef.current) return;
            // Blocked: the confirm screen never renders over the blocked one, so
            // Escape closes outright instead of toggling a flag nobody sees.
            if (checkoutBlockedRef.current) {
                closeNowRef.current?.();
                return;
            }
            setShowLeaveConfirm((showing) => {
                if (showing) return false;
                return true;
            });
        };
        window.addEventListener('keydown', onKey);
        return () => {
            document.body.style.overflow = prev;
            // If another layer already locked scroll, restoring `prev` can
            // leave the body stuck hidden after SPA navigation. Clearing when
            // the saved value was hidden avoids that stale lock.
            if (prev === 'hidden') {
                document.body.style.removeProperty('overflow');
            }
            window.removeEventListener('keydown', onKey);
        };
    }, [open]);

    useEffect(() => () => scheduleDocumentScrollLockRelease(), []);

    const closeNow = () => {
        setShowLeaveConfirm(false);
        onClose?.();
    };
    closeNowRef.current = closeNow; // keep the Escape handler on the current closure
    /**
     * Ask before leaving — EXCEPT when checkout is blocked, where closing is
     * immediate.
     *
     * The blocked screen is rendered ahead of the leave-confirm screen, so
     * raising the confirm from there sets a flag nothing displays: the X, the
     * backdrop and Escape all appeared to do nothing and the buyer was stuck on
     * "Checkout Restricted" with no way out. There is also nothing to confirm —
     * the purchase cannot proceed, so no selection is at risk.
     */
    const requestClose = () => {
        if (checkoutBlocked) {
            closeNow();
            return;
        }
        setShowLeaveConfirm(true);
    };
    const cancelLeave = () => setShowLeaveConfirm(false);
    const confirmLeave = () => closeNow();
    const handleBackdropClick = () => {
        if (showLeaveConfirm) cancelLeave();
        else requestClose();
    };

    // Expand the per-tier counts into a flat cart (one entry per ticket), the
    // shape the Payment flow + backend expect.
    const selectedTicket = useMemo(() => {
        const cart = [];
        (ticketList || []).forEach((t, i) => {
            const qty = counts[i] || 0;
            for (let n = 0; n < qty; n++) cart.push(t);
        });
        return cart;
    }, [ticketList, counts]);

    // The attendee name is optional. When it is skipped both name fields go up
    // as null and are stored as NULL — the front end renders "Guest" for those.
    //
    // They used to be backfilled from the account username, falling back to the
    // local part of the email, which wrote the PURCHASER'S identity into the
    // ATTENDEE fields ("aruns.unlock2go" as both first and last name) and then
    // printed it on the ticket as the holder. Who bought the ticket is already
    // recorded in id_user / email / username and is untouched by this.
    const buildTicketUserDetails = () => {
        const u = user && typeof user === 'object' ? user : {};
        const buyerEmail = String(email || u.email || '').trim();
        const buyerPhone = String(phone || u.phone || '').trim();
        return selectedTicket.map((_, i) => {
            const h = holderDetails[i] || {};
            const first = showHolderNames ? String(h.firstName || '').trim() : '';
            const last = showHolderNames ? String(h.lastName || '').trim() : '';
            // A first name alone is a valid attendee; a last name alone is not
            // enough to name anyone, so it is dropped with the rest.
            const named = first !== '';
            return {
                firstName: named ? first : null,
                lastName: named ? (last || null) : null,
                email: buyerEmail,
                confirm_email: buyerEmail,
                phone: buyerPhone,
            };
        });
    };

    const syncCheckoutToStore = () => {
        if (selectedTicket.length === 0) return;
        dispatch(setSelectedTicket({ selectedTicket, ticketUserDetails: buildTicketUserDetails() }));
    };

    useEffect(() => {
        if (step !== 2 || selectedTicket.length === 0) return;
        syncCheckoutToStore();
    }, [step, selectedTicket, email, phone, holderDetails, showHolderNames]);

    // Push the contact details onto the fraud session once the buyer has typed
    // them. The session row is created by proceedToCheckout() on the way INTO
    // this step, so a guest's email isn't known yet at that point — without this
    // fraud_sessions.email stays NULL for the whole guest checkout and Layer 3's
    // email checks (disposable domain, typo, MX, email blacklist) never run.
    // Debounced so it fires on a settled address, not on every keystroke.
    useEffect(() => {
        if (step !== 2) return undefined;
        const address = String(email || user?.email || '').trim();
        if (!address) return undefined;
        const t = setTimeout(() => {
            syncCheckoutContact({ email: address, phone: String(phone || user?.phone || '').trim() });
        }, 900);
        return () => clearTimeout(t);
    }, [step, email, phone]);

    // Seed contact details from the account ONCE per open, tracked by these refs
    // rather than by "is the field empty".
    //
    // The effect below lists `email`/`phone` in its own deps, so it re-runs on
    // every keystroke. Gated only on emptiness, it re-filled the field the
    // instant a logged-in buyer cleared it to type a different address — the
    // account email reappeared on the delete of the last character, so the field
    // could never actually be changed.
    //
    // Gated on the account value being present (not on a "did we run" flag) so a
    // guest who logs in partway through the flow still gets seeded when the user
    // object finally arrives.
    const prefilledEmailRef = useRef(false);
    const prefilledPhoneRef = useRef(false);

    useEffect(() => {
        if (step !== 2) return;
        const u = user && typeof user === 'object' ? user : {};
        if (!prefilledEmailRef.current && u.email) {
            prefilledEmailRef.current = true;
            if (!email) setEmail(u.email);
        }
        if (!prefilledPhoneRef.current && u.phone) {
            prefilledPhoneRef.current = true;
            if (!phone) setPhone(u.phone);
        }
        if (holderDetails.length !== selectedTicket.length) {
            setHolderDetails(selectedTicket.map(() => ({ firstName: '', lastName: '' })));
        }
    }, [step, selectedTicket.length, user, email, phone, holderDetails.length]);

    const holderRows = useMemo(() => {
        const typeCount = {};
        return selectedTicket.map((ticket, index) => {
            const key = ticket.name;
            typeCount[key] = (typeCount[key] || 0) + 1;
            return { index, ticket, ticketNum: typeCount[key] };
        });
    }, [selectedTicket]);

    const filledHolderCount = holderDetails.filter(
        (h) => String(h?.firstName || '').trim() && String(h?.lastName || '').trim()
    ).length;

    const fees = useMemo(() => computeCartFees(selectedTicket, feeConfig), [selectedTicket, feeConfig]);
    // The single "Fees" line both the desktop and mobile summaries show: the two
    // fees the BUYER is charged, disclosed together (host-absorbed fees are not
    // in `fees` at all).
    const buyerFees = (fees.serviceFee || 0) + (fees.paymentFee || 0);
    const totalQty = counts.reduce((a, b) => a + (b || 0), 0);
    const cur = currency || 'JMD';
    const fmt = (n) => new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(Number(n) || 0);

    // Format an ISO8601 ticket sale boundary (sale_starts / sale_ends) for the
    // "Sales start" / "Sales end" callouts, e.g. "Jun 20, 2026, 10:00 AM".
    const formatSaleDateTime = (iso) => {
        if (!iso) return '';
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return '';
        return d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
    };

    // Whether the event itself is already over — a past event's tiers are
    // treated as ended so they never show "Sales start" / "On Sale".
    const eventEnded = (() => {
        const datePart = eventDetails?.end_date || eventDetails?.date;
        if (!datePart) return false;
        const [y, m, d] = String(datePart).split('-').map(Number);
        if (!y || !m || !d) return false;
        const [hh, mm] = (eventDetails?.end_time || eventDetails?.time || '23:59').slice(0, 5).split(':').map(Number);
        return new Date() > new Date(y, m - 1, d, hh || 0, mm || 0);
    })();

    // Resolve the full sale-window state for a tier, mirroring the event page
    // and the legacy booking modal so all three surfaces agree.
    const saleStateOf = (t) => {
        const now = new Date();
        const saleStarts = t.sale_starts ? new Date(t.sale_starts) : null;
        const saleEnds = t.sale_ends ? new Date(t.sale_ends) : null;
        const saleWindowEnded = saleEnds && !Number.isNaN(saleEnds.getTime()) && now > saleEnds;
        const saleEnded = saleWindowEnded || eventEnded;
        const saleNotStarted = saleStarts && !Number.isNaN(saleStarts.getTime()) && now < saleStarts && !saleEnded;
        const hasSaleEnd = saleEnds && !Number.isNaN(saleEnds.getTime());
        return { saleStarts, saleEnds, saleWindowEnded, saleEnded, saleNotStarted, hasSaleEnd };
    };

    const statusOf = (t) => {
        if (t.is_sold_out) return { label: 'SOLD OUT', tone: 'text-red-500', dot: 'bg-red-500', disabled: true };
        const { saleEnded, saleNotStarted } = saleStateOf(t);
        if (saleEnded) return { label: 'SALES ENDED', tone: 'text-red-500', dot: 'bg-red-500', disabled: true };
        if (saleNotStarted) return { label: 'NOT ON SALE YET', tone: 'text-blue-500', dot: 'bg-blue-400', disabled: true };
        if (!t.is_on_sale) return { label: 'NOT ON SALE', tone: 'text-gray-400', dot: 'bg-gray-300', disabled: true };
        return { label: 'ON SALE NOW', tone: 'text-emerald-600', dot: 'bg-emerald-500', disabled: false };
    };

    const increment = (i, t) => {
        if (totalQty >= MAX_PER_ORDER) {
            toast.error(`You can book only ${MAX_PER_ORDER} tickets at a time.`);
            return;
        }
        const available = t.is_unlimited ? null : Number(t.qty);
        if (available != null && (counts[i] || 0) >= available) {
            toast.error('No more tickets available.');
            return;
        }
        setCounts((prev) => { const next = [...prev]; next[i] = (next[i] || 0) + 1; return next; });
    };

    const decrement = (i) => {
        setCounts((prev) => { const next = [...prev]; next[i] = Math.max(0, (next[i] || 0) - 1); return next; });
    };

    // Open a checkout session so the server can score the attempt before the
    // buyer reaches payment. A hard block surfaces the restriction screen with a
    // Contact Support option; anything else advances to checkout. Fails open —
    // any error here still lets the buyer through (payment re-gates server-side).
    const proceedToCheckout = async () => {
        // Re-entry guard: ignore a second call while the first is still running
        // (double-click, or the login-close effect firing alongside goCheckout).
        if (checkoutInFlight.current) return;
        checkoutInFlight.current = true;
        syncCheckoutToStore();
        setCheckingBlock(true);
        let session = null;
        try {
            session = await startCheckoutSession({
                event_id: eventDetails?.id,
                user_id: user?.id,
                email: String(email || user?.email || '').trim(),
                phone: String(phone || user?.phone || '').trim(),
                currency: cur,
                amount: Number(fees.total) || 0,
                quantity: selectedTicket.length,
                tickets: selectedTicket,
            });
        } catch (_) {
            session = null;
        }
        setCheckingBlock(false);
        checkoutInFlight.current = false;
        if (session?.blocked) {
            setBlockMessage(session.message || '');
            setCheckoutBlocked(true);
            return;
        }
        setStep(2);
    };

    const goCheckout = () => {
        if (selectedTicket.length === 0) {
            toast.error('Please select at least one ticket');
            return;
        }
        if (!hasAuthSession) {
            dispatch(openModal());
            return;
        }
        proceedToCheckout();
    };

    const contactSupport = () => {
        const subject = encodeURIComponent(`Checkout restricted — ${eventDetails?.name || 'Ticket purchase'}`);
        window.location.href = `mailto:${SUPPORT_EMAIL}?subject=${subject}`;
    };

    const handlePaymentClose = () => {
        setStep(1);
        onClose?.();
    };

    // OK on the expiry dialog: the modal is already hidden, so send it all the
    // way out rather than back to ticket selection where it would stay hidden.
    const handleExpiredDismiss = () => {
        setReservationExpired(false);
        handlePaymentClose();
    };

    // Resume checkout once the login modal closes and the user is authed.
    useEffect(() => {
        const justClosed = loginWasOpen.current && !loginModalOpen;
        loginWasOpen.current = loginModalOpen;
        if (justClosed && open && hasAuthSession && selectedTicket.length > 0) {
            proceedToCheckout();
        }
    }, [loginModalOpen, open, hasAuthSession, selectedTicket]);

    const formatDateLong = (d) => {
        if (!d) return '';
        const [y, m, day] = String(d).split('T')[0].split('-').map(Number);
        if (!y || !m || !day) return d;
        return new Date(y, m - 1, day).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
    };
    const timeRange = () => {
        const s = (eventDetails?.time || '').slice(0, 5);
        const e = (eventDetails?.end_time || '').slice(0, 5);
        if (!s) return '';
        return e ? `${s} - ${e}` : s;
    };

    if (!open) return null;

    const cover = eventDetails?.horizontal_flyer || eventDetails?.cover_image || eventDetails?.flyer1 || '';

    const checkoutLabel = Number(fees.total) > 0 ? 'Continue to Checkout' : 'Continue to Checkout';
    const payNowLabel = Number(fees.total) > 0 ? `Pay Now ${cur} ${fmt(fees.total)}` : 'Get Free Ticket';
    // While the payment is processing, Payment reports a busy label (e.g.
    // "Verifying…", "Initiating payment...") and disables the action — mirror
    // both onto the sidebar's Pay Now button so the first click shows loading.
    const payProcessing = payState.disabled;
    const payNowDisplay = payProcessing && payState.button ? payState.button : payNowLabel;
    // A full-cover blocking overlay is shown while checkout is resuming after
    // login (`checkingBlock`) or a payment is in flight (`payProcessing`), so the
    // buyer can't click, edit the cart, or double-submit mid-process. Not shown
    // over the block/leave screens, which have their own actions.
    const checkoutBusy = !checkoutBlocked && !showLeaveConfirm && (checkingBlock || payProcessing);
    const busyLabel = checkingBlock
        ? 'Completing checkout…'
        : (payState.button || 'Processing your payment…');
    // Guests must supply a phone number (at least 7 digits) — it's collected at
    // checkout since there's no account to pull it from. Logged-in buyers are
    // unaffected (their phone, if any, prefills from the account).
    const guestPhoneMissing = isGuest && phone.replace(/\D/g, '').length < 7;
    const payNowDisabled = !email.trim() || guestPhoneMissing || payProcessing;
    const inputClass = 'w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm font-medium text-gray-900 outline-none focus:border-gray-400 placeholder:text-gray-400';
    const holderInputClass = 'w-full min-h-[42px] sm:min-h-[46px] rounded-xl border border-gray-200 bg-white px-3 sm:px-4 py-3 sm:py-3.5 text-sm sm:text-[15px] font-medium text-gray-900 outline-none focus:border-gray-400 placeholder:text-gray-400';

    const ticketRows = (ticketList || []).map((t, i) => {
        const st = statusOf(t);
        const sale = saleStateOf(t);
        const isFree = Number(t.fee) <= 0;
        const qty = counts[i] || 0;
        return (
            <div key={`${t.name}-${i}`} className={`relative rounded-2xl border border-gray-200 pl-4 pr-3 py-3.5 md:pl-5 md:pr-4 md:py-4 overflow-hidden bg-white${i === 0 ? ' mt-2 md:mt-0' : ''}`}>
                <span className="absolute left-0 top-0 bottom-0 w-1 bg-emerald-500" aria-hidden />
                <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between md:gap-4">
                    <div className="min-w-0 flex-1">
                        <span className={`inline-flex items-center gap-1.5 text-[10px] md:text-[11px] font-extrabold tracking-wide ${st.tone}`}>
                            <span className={`w-2 h-2 rounded-full shrink-0 ${st.dot}`} />
                            {st.label}
                        </span>
                        <h4 className="m-0 mt-1 text-base md:text-lg font-extrabold text-[#0f172a] break-words">{t.name}</h4>
                        {t.description && (
                            <p className="m-0 text-xs md:text-sm font-medium text-gray-500 line-clamp-3 md:line-clamp-2">{t.description}</p>
                        )}
                        {/* Face value only. The per-ticket "+X Fee" hint that sat
                            beside it was removed — fees are now disclosed once,
                            as the combined "Fees" line in the Order Summary. */}
                        <div className="mt-1.5 flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                            <span className="text-sm md:text-base font-extrabold text-[#0f172a]">
                                {isFree ? 'Free' : `${cur} ${fmt(t.fee)}`}
                            </span>
                        </div>
                        {sale.saleNotStarted && (
                            <div className="mt-2 flex w-full max-w-full items-start gap-1.5 rounded-lg bg-blue-50 px-2.5 py-1.5 text-[10px] md:text-[11px] font-semibold text-gray-600">
                                <Calendar className="w-3.5 h-3.5 text-blue-500 shrink-0 mt-0.5" />
                                <span className="min-w-0 break-words">Sales start: <span className="text-blue-600">{formatSaleDateTime(t.sale_starts)}</span></span>
                            </div>
                        )}
                        {!sale.saleNotStarted && sale.hasSaleEnd && (
                            <div className={`mt-2 flex w-full max-w-full items-start gap-1.5 rounded-lg px-2.5 py-1.5 text-[10px] md:text-[11px] font-semibold text-gray-600 ${sale.saleWindowEnded ? 'bg-red-50' : 'bg-amber-50'}`}>
                                <Calendar className={`w-3.5 h-3.5 shrink-0 mt-0.5 ${sale.saleWindowEnded ? 'text-red-500' : 'text-amber-500'}`} />
                                <span className="min-w-0 break-words">
                                    {sale.saleWindowEnded ? 'Sales ended: ' : 'Sales end: '}
                                    <span className={sale.saleWindowEnded ? 'text-red-600' : 'text-amber-600'}>{formatSaleDateTime(t.sale_ends)}</span>
                                </span>
                            </div>
                        )}
                    </div>
                    <div className="flex items-center justify-between md:justify-end gap-2 shrink-0 md:self-center">
                        <span className="text-xs font-bold text-gray-500 md:hidden">Quantity</span>
                        <div className="flex items-center gap-2">
                            <button
                                type="button"
                                onClick={() => decrement(i)}
                                disabled={qty <= 0}
                                aria-label="Decrease"
                                className="w-10 h-10 rounded-lg bg-gray-100 border-none flex items-center justify-center cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                <Minus className="w-4 h-4 text-gray-700" />
                            </button>
                            <span className="w-6 text-center text-lg font-extrabold text-[#0f172a]">{qty}</span>
                            <button
                                type="button"
                                onClick={() => increment(i, t)}
                                disabled={st.disabled}
                                aria-label="Increase"
                                className="w-10 h-10 rounded-lg bg-[#ffe500] border-none flex items-center justify-center cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                <Plus className="w-4 h-4 text-black" />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    });

    const orderLineItems = (ticketList || []).map((t, i) => {
        const qty = counts[i] || 0;
        if (qty <= 0) return null;
        const lineFee = Math.max(0, Number(t.fee) || 0) * qty;
        return (
            <div key={`sum-${t.name}-${i}`} className="flex items-start justify-between gap-3 text-sm">
                <span className="text-gray-700 font-semibold break-words min-w-0">{qty}x {t.name}</span>
                <span className="font-bold text-[#0f172a] shrink-0">{Number(t.fee) <= 0 ? 'Free' : `${cur} ${fmt(lineFee)}`}</span>
            </div>
        );
    });

    const modal = (
        <div
            className={`fixed inset-0 z-[200] flex items-end md:items-center justify-center md:p-4 lg:p-6 ${reservationExpired ? 'invisible pointer-events-none' : ''}`}
            aria-hidden={reservationExpired || undefined}
        >
            <div
                className="hidden md:block absolute inset-0 bg-black/40"
                onClick={handleBackdropClick}
                aria-hidden
            />

            <div
                className={`relative z-10 w-full max-w-[1100px] h-[100dvh] max-h-[100dvh] md:h-[min(88vh,900px)] md:max-h-[min(88vh,900px)] bg-white md:rounded-3xl shadow-2xl overflow-hidden flex flex-col ${showLeaveConfirm || checkoutBlocked ? '' : 'md:flex-row md:flex-nowrap'} items-stretch animate-ts-slide-up md:animate-ts-pop-in`}
                role="dialog"
                aria-modal="true"
                aria-labelledby={checkoutBlocked ? 'checkout-blocked-title' : showLeaveConfirm ? 'leave-checkout-title' : 'ticket-select-title'}
            >
                <style>{`
                    @keyframes ts-slide-up { 0% { transform: translateY(100%); } 100% { transform: translateY(0); } }
                    @keyframes ts-pop-in { 0% { opacity: 0; transform: scale(0.97); } 100% { opacity: 1; transform: scale(1); } }
                    .animate-ts-slide-up { animation: ts-slide-up 0.35s ease-out; }
                    .animate-ts-pop-in { animation: ts-pop-in 0.35s ease-out; }
                `}</style>

                {/* Blocking overlay: sits above the whole modal card while the
                    checkout is resuming after login or a payment is processing,
                    so nothing behind it can be clicked, edited, or double-submitted
                    until the process completes. */}
                {checkoutBusy && (
                    <div
                        className="absolute inset-0 z-[60] flex flex-col items-center justify-center gap-4 bg-white/85 backdrop-blur-sm px-6 text-center cursor-wait"
                        role="alert"
                        aria-live="assertive"
                        aria-busy="true"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div
                            className="h-12 w-12 rounded-full border-4 border-gray-200 border-t-[#ffe500] animate-spin"
                            aria-hidden
                        />
                        <p className="m-0 text-base font-extrabold text-[#0f172a]">{busyLabel}</p>
                        <p className="m-0 text-sm font-medium text-gray-500">
                            Please don't close or refresh this page.
                        </p>
                    </div>
                )}

                {checkoutBlocked ? (
                    <div
                        className="relative flex flex-1 flex-col items-center justify-center w-full min-h-0 px-6 py-10 sm:px-10 sm:py-12 text-center"
                        role="alertdialog"
                        aria-describedby="checkout-blocked-desc"
                    >
                        <button
                            type="button"
                            onClick={requestClose}
                            aria-label="Close"
                            className="absolute top-4 right-4 sm:top-6 sm:right-6 w-9 h-9 rounded-full border border-gray-200 bg-white flex items-center justify-center cursor-pointer hover:bg-gray-50"
                        >
                            <X className="w-5 h-5 text-gray-700" />
                        </button>

                        <span className="flex items-center justify-center w-16 h-16 rounded-full bg-red-50">
                            <ShieldCheck className="w-8 h-8 text-red-500" />
                        </span>

                        <h2 id="checkout-blocked-title" className="m-0 mt-5 text-2xl sm:text-[26px] font-extrabold text-[#0f172a]">
                            Checkout Restricted
                        </h2>
                        <p id="checkout-blocked-desc" className="m-0 mt-3 text-sm sm:text-[15px] font-medium text-gray-500 leading-relaxed max-w-md">
                            {blockMessage || "We’re unable to complete this purchase due to security restrictions on this checkout. Please contact Apsession Support if you believe this is an error."}
                        </p>

                        <div className="mt-8 flex flex-col sm:flex-row items-center gap-3 w-full max-w-sm">
                            <button
                                type="button"
                                onClick={() => setCheckoutBlocked(false)}
                                className="w-full sm:flex-1 h-12 rounded-xl border-2 border-[#0f172a] bg-white font-bold text-[#0f172a] text-base cursor-pointer hover:bg-gray-50"
                            >
                                Go Back
                            </button>
                            <button
                                type="button"
                                onClick={contactSupport}
                                className="w-full sm:flex-1 inline-flex items-center justify-center gap-2 h-12 rounded-xl border-none bg-[#ffe500] font-bold text-[#0f172a] text-base cursor-pointer hover:brightness-95"
                            >
                                <LifeBuoy className="w-5 h-5 shrink-0" />
                                Contact Support
                            </button>
                        </div>
                    </div>
                ) : showLeaveConfirm ? (
                    <div
                        className="relative flex flex-1 flex-col items-center justify-center w-full min-h-0 px-6 py-10 sm:px-10 sm:py-12 text-center"
                        role="alertdialog"
                        aria-describedby="leave-checkout-desc"
                    >
                        <button
                            type="button"
                            onClick={cancelLeave}
                            aria-label="Close"
                            className="absolute top-4 right-4 sm:top-6 sm:right-6 w-9 h-9 rounded-full border border-gray-200 bg-white flex items-center justify-center cursor-pointer hover:bg-gray-50"
                        >
                            <X className="w-5 h-5 text-gray-700" />
                        </button>

                        <img
                            src="https://apsession.s3.us-east-1.amazonaws.com/Apsession+won+icon.png"
                            alt="Apsession"
                            className="w-25 object-contain p-2 rounded-xl"
                        />

                        <h2 id="leave-checkout-title" className="m-0 mt-5 text-2xl sm:text-[26px] font-extrabold text-[#0f172a]">
                            {step === 2 ? 'Leave Checkout?' : 'Leave ticket selection?'}
                        </h2>
                        <p id="leave-checkout-desc" className="m-0 mt-3 text-sm sm:text-[15px] font-medium text-gray-500 leading-relaxed max-w-md">
                            Are you sure you want to leave checkout? The items you&apos;ve selected may not be available later.
                        </p>

                        <div className="mt-8 flex items-center gap-3 w-full max-w-sm">
                            <button
                                type="button"
                                onClick={cancelLeave}
                                className="flex-1 h-12 rounded-xl border-2 border-[#0f172a] bg-white font-bold text-[#0f172a] text-base cursor-pointer hover:bg-gray-50"
                            >
                                Stay
                            </button>
                            <button
                                type="button"
                                onClick={confirmLeave}
                                className="flex-1 h-12 rounded-xl border-none bg-[#ffe500] font-bold text-[#0f172a] text-base cursor-pointer hover:brightness-95"
                            >
                                Leave
                            </button>
                        </div>
                    </div>
                ) : (
                <>
                {/* Mobile header */}
                <div
                    className="md:hidden relative flex items-center bg-[#ffea00] shrink-0 border-b border-yellow-500/30"
                    style={modalHeaderPaddingStyle}
                >
                    <button
                        type="button"
                        onClick={requestClose}
                        aria-label="Close"
                        className="relative z-10 shrink-0 w-9 h-9 rounded-full border-none bg-black/5 flex items-center justify-center cursor-pointer"
                    >
                        <X className="w-5 h-5 text-black" />
                    </button>
                    <h2
                        id="ticket-select-title"
                        className="absolute inset-x-0 m-0 text-lg font-black text-black text-center truncate px-12 pointer-events-none"
                    >
                        {step === 1 ? 'Select Tickets' : 'Checkout'}
                    </h2>
                </div>

                {/* Tickets column */}
                <div className="flex flex-col flex-1 min-w-0 basis-0 overflow-hidden md:h-full md:min-h-0">
                    <div className="hidden md:block shrink-0 p-6 lg:p-8 pb-0">
                        <div className="flex items-start justify-between gap-4">
                            <div className="flex items-center gap-3 min-w-0">
                                {/* <div className="w-12 h-12 rounded-xl bg-[#ffe500] flex items-center justify-center fon  t-black text-black shrink-0">AP</div> */}
                                <img src="/assets/images/logo.svg" alt="logo" className="w-10 h-10 rounded-xl object-contain shrink-0 bg-[#ffe500] p-2 rounded-lg" />
                                <div className="min-w-0">
                                    <h2 className="m-0 text-2xl font-extrabold text-[#0f172a] leading-tight">
                                        {step === 1 ? 'Select Tickets' : 'Checkout'}
                                    </h2>
                                    {/* <p className="m-0 text-sm font-medium text-gray-500">
                                        {step === 1
                                            ? 'Choose your tickets and complete your purchase'
                                            : 'Almost there! Complete your payment to secure your tickets.'}
                                    </p> */}
                                </div>
                            </div>
                            <button type="button" onClick={requestClose} aria-label="Close" className="shrink-0 w-9 h-9 rounded-lg border border-gray-200 flex items-center justify-center bg-white cursor-pointer">
                                <X className="w-5 h-5 text-gray-700" />
                            </button>
                        </div>
                    </div>

                    {/* Reservation countdown — always on screen while checkout is
                        open, on both steps, directly under the header so it stays
                        pinned rather than scrolling away with the content. Shown
                        with an empty cart too: the clock is running from the
                        moment checkout opens, so hiding it until the first ticket
                        was picked told the buyer they had more time than they did. */}
                    <div className="flex shrink-0 items-center justify-center h-[38px] bg-[#fff2f2]">
                        <p className="m-0 text-sm font-semibold text-[#e53935] leading-none inline-flex items-center gap-1.5">
                            <span className="text-sm leading-none" aria-hidden="true">⏳</span>
                            Tickets reserved for <span ref={timerTextRef}>{formatTimer(RESERVATION_SECONDS)}</span>
                        </p>
                    </div>

               {step === 1 ?    <>
                {/* Step indicator + event summary card — desktop/tablet only.
                    Hidden on mobile so the ticket list starts right below the header. */}
                <div className="hidden md:block shrink-0 px-4 pt-3 pb-3 md:px-6 lg:px-8 md:pt-0" />

                    <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden overscroll-contain touch-pan-y px-4 md:px-6 lg:px-8 py-1 pb-4 md:pb-6 [-webkit-overflow-scrolling:touch]">
                        <div className="flex flex-col gap-3 w-full">
                            {(ticketList || []).length === 0 ? (
                                <div className="rounded-2xl border border-gray-200 p-6 text-center text-gray-500 font-medium">
                                    No tickets available for this event.
                                </div>
                            ) : ticketRows}
                        </div>
                    </div>
                    </>
                    : (
                    <>
                        <div className="hidden md:block shrink-0 px-4 pt-3 pb-2 md:px-6 lg:px-8 md:pt-0" />

                        <div
                            className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden overscroll-contain touch-pan-y px-4 md:px-6 lg:px-8 pb-4 md:pb-6 space-y-5 [-webkit-overflow-scrolling:touch]"
                            style={isMobileViewport ? { paddingTop: '1rem' } : undefined}
                        >
                            {/* Contact Information */}
                            <section className="rounded-2xl border border-gray-200 bg-white p-3 md:p-5">
                                <h3 className="m-0 text-base font-extrabold text-[#0f172a]">Contact Information</h3>
                                <label className="block mt-3 text-sm font-semibold text-gray-700">
                                    Email Address <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="email"
                                    required
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className={`${inputClass} mt-2`}
                                    placeholder="you@example.com"
                                />
                                <p className="m-0 mt-2 text-xs font-medium text-gray-500">
                                    Your tickets and order confirmation will be sent here.
                                </p>

                                <label className="block mt-4 text-sm font-semibold text-gray-700">
                                    Phone Number {isGuest && <span className="text-red-500">*</span>}
                                </label>
                                <input
                                    type="tel"
                                    inputMode="tel"
                                    pattern="[+]?[0-9]*"
                                    required={isGuest}
                                    value={phone}
                                    // Digits plus an optional leading "+" so
                                    // international numbers (e.g. +919540149333)
                                    // are accepted. Letters, spaces, and other
                                    // symbols are stripped as typed or pasted.
                                    onChange={(e) => {
                                        const raw = e.target.value;
                                        const plus = raw.trim().startsWith('+') ? '+' : '';
                                        setPhone(plus + raw.replace(/\D/g, ''));
                                    }}
                                    className={`${inputClass} mt-2`}
                                    placeholder="+15551234567"
                                    autoComplete="tel"
                                />
                                <p className="m-0 mt-2 text-xs font-medium text-gray-500">
                                    Used to reach you about your order.
                                </p>
                            </section>

                            {/* Ticket Holder Names */}
                            <section className="rounded-2xl border border-gray-200 bg-white p-3 md:p-5">
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                        <div className="flex flex-wrap items-center gap-2">
                                            <h3 className="m-0 text-base font-extrabold text-[#0f172a]">Ticket Holder Names (Optional)</h3>
                                            {showHolderNames && totalQty > 0 && (
                                                <span className="inline-flex items-center rounded-full bg-emerald-50 px-2.5 py-0.5 text-[11px] font-bold text-emerald-600">
                                                    {filledHolderCount} of {totalQty} added
                                                </span>
                                            )}
                                        </div>
                                        <p className="m-0 mt-1 text-xs font-medium text-gray-500">
                                            Add names for each ticket. These names may be checked at the door.
                                        </p>
                                    </div>
                                    <button
                                        type="button"
                                        role="switch"
                                        aria-checked={showHolderNames}
                                        onClick={() => setShowHolderNames((v) => !v)}
                                        className={`relative shrink-0 w-11 h-6 rounded-full border-none cursor-pointer transition-colors ${showHolderNames ? 'bg-[#ffe500]' : 'bg-gray-200'}`}
                                    >
                                        <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${showHolderNames ? 'translate-x-5' : ''}`} />
                                    </button>
                                </div>

                                {showHolderNames && (
                                    <div className="mt-4 flex flex-col gap-3 sm:gap-4">
                                        {holderRows.map(({ index, ticket, ticketNum }) => {
                                            const h = holderDetails[index] || { firstName: '', lastName: '' };
                                            const complete = String(h.firstName || '').trim() && String(h.lastName || '').trim();
                                            return (
                                                <div
                                                    key={`holder-${index}`}
                                                    className="grid grid-cols-[28px_1fr_1fr] grid-rows-[auto_auto] gap-x-2 gap-y-2.5 sm:grid-cols-[30px_4.75rem_minmax(0,1fr)_minmax(0,1fr)_22px] sm:grid-rows-1 sm:gap-x-2 sm:gap-y-0 sm:items-center"
                                                >
                                                    <span
                                                        className={`row-span-2 self-center w-7 h-7 sm:w-8 sm:h-8 rounded-full text-[11px] sm:text-xs font-extrabold flex items-center justify-center shrink-0 ${
                                                            complete ? 'bg-emerald-500 text-white' : 'bg-gray-100 text-gray-500'
                                                        }`}
                                                    >
                                                        {index + 1}
                                                    </span>
                                                    <div className="col-start-2 col-span-1 min-w-0 sm:max-w-[4.75rem]">
                                                        <p className="m-0 text-[14px] sm:text-xs font-extrabold text-[#0f172a] leading-tight truncate">
                                                            {ticket.name}
                                                        </p>
                                                        <p className="m-0 text-[10px] sm:text-[11px] font-medium text-gray-500 leading-tight">
                                                            Ticket #{ticketNum}
                                                        </p>
                                                    </div>
                                                    <span className="col-start-3 row-start-1 self-start mt-0.5 sm:col-start-5 sm:row-start-1 sm:mt-0 sm:self-center flex items-center justify-center shrink-0 w-5 h-5">
                                                        {complete ? (
                                                            <Check className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-500" aria-hidden stroke-width={4}
                                                            style={{border:"2px solid #10b981",borderRadius:"50%",padding:"2px",color:"#10b981"}}
                                                            />
                                                        ) : null}
                                                    </span>
                                                    <input
                                                        type="text"
                                                        value={h.firstName}
                                                        onChange={(e) => setHolderDetails((prev) => {
                                                            const next = [...prev];
                                                            next[index] = { ...next[index], firstName: e.target.value };
                                                            return next;
                                                        })}
                                                        className={`${holderInputClass} col-start-2 row-start-2 sm:col-start-3 sm:row-start-1`}
                                                        placeholder="First name"
                                                    />
                                                    <input
                                                        type="text"
                                                        value={h.lastName}
                                                        onChange={(e) => setHolderDetails((prev) => {
                                                            const next = [...prev];
                                                            next[index] = { ...next[index], lastName: e.target.value };
                                                            return next;
                                                        })}
                                                        className={`${holderInputClass} col-start-3 row-start-2 sm:col-start-4 sm:row-start-1`}
                                                        placeholder="Last name"
                                                    />
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </section>

                            {/* Payment (coupon widget is portalled into the
                                Order Summary column via couponSlot). */}
                            <section className="rounded-2xl border border-gray-200 bg-white p-3 md:p-5 min-w-0 overflow-x-hidden">
                                <div className="min-w-0 overflow-x-hidden [&>div]:!flex-none [&>div]:h-auto [&_[class*='overflow-y-auto']]:overflow-x-hidden">
                                <Payment
                                    embedded
                                    hideTimer
                                    hidePayFooter={Number(fees.total) > 0}
                                    openPayment={step === 2}
                                    handleClose={handlePaymentClose}
                                    onBack={() => setStep(1)}
                                    onPayStateChange={setPayState}
                                    couponPortalTarget={couponSlot}
                                    // This modal owns the clock (it spans both
                                    // steps); <Payment> follows this flag and
                                    // handles the expiry dialog + refund.
                                    expired={reservationExpired}
                                    onExpire={() => setReservationExpired(true)}
                                    onExpiredDismiss={handleExpiredDismiss}
                                >
                                    <div className="border-t border-gray-100">
                                        <h3 className="m-0 text-base font-extrabold text-[#0f172a]">Payment Method</h3>
                                        <p className="m-0 mt-1 text-xs font-medium text-gray-500 inline-flex items-center gap-1.5">
                                            <Lock className="w-3.5 h-3.5 shrink-0" />
                                            All payments are secure and encrypted.
                                        </p>                                      
                                    </div>
                                </Payment>
                                </div>
                            </section>

                            {/* Coupon Code — scrolls with form on mobile */}
                            <div className="md:hidden pb-2">
                                <h4 className="m-0 mb-2 text-base font-extrabold text-[#0f172a]">Coupon Code</h4>
                                <div
                                    ref={attachMobileCouponSlot}
                                    className="min-w-0 overflow-x-hidden [&_[class*='overflow-y-auto']]:overflow-x-hidden"
                                />
                            </div>

                            {/* Back to Tickets — scrolls with form on mobile */}
                            <div className="md:hidden py-2">
                                <button
                                    type="button"
                                    onClick={() => setStep(1)}
                                    className="inline-flex items-center btn btn-outline-dark py-2 px-2 gap-1.5 text-sm font-bold text-gray-600 bg-transparent border-1 border-gray-200 rounded-md cursor-pointer hover:text-gray-900 p-0"
                                >
                                    ← Back to Tickets
                                </button>
                            </div>
                        </div>

                        <div className="hidden md:flex shrink-0 flex-col md:flex-row md:items-center md:justify-between gap-3 py-2 px-4 md:p-4">
                            <button
                                type="button"
                                onClick={() => setStep(1)}
                                className="inline-flex items-center btn btn-outline-dark py-2 px-2 gap-1.5 text-sm font-bold text-gray-600 bg-transparent border-1 border-gray-200 rounded-md cursor-pointer hover:text-gray-900 p-0"
                            >
                                ← Back to Tickets
                            </button>
                            {/* <p className="m-0 text-xs font-medium text-gray-400 flex items-center justify-center gap-1.5 md:justify-end">
                                <ShieldCheck className="w-3.5 h-3.5 shrink-0" />
                                Secure checkout powered by Apsession
                            </p> */}
                        </div>
                    </>
                    )}

                    <div className="hidden md:flex shrink-0 items-center gap-2 text-sm font-medium text-gray-500 px-6 lg:px-8 pb-6 bg-[#fdfdfd] pt-2 align-items-center ">
                        {step === 1 && (
                            <>
                                <ShieldCheck className="w-4 h-4 text-gray-400" />
                                <span className="inline-flex items-center gap-1.5">Secure checkout powered by {gatewayBadge()}</span>
                            </>
                        )}
                    </div>
                </div>

                {/* Summary column — desktop sidebar + mobile sticky footer */}
                <div className="shrink-0 w-full md:w-[380px] md:min-w-[380px] md:max-w-[380px] md:h-full md:min-h-0 bg-[#ffffff] border-t md:border-t-0 md:border-l border-gray-200 flex flex-col overflow-hidden shadow-[0_-8px_24px_rgba(0,0,0,0.06)] md:shadow-none">
                    <div className="hidden md:flex flex-1 min-h-0 flex-col overflow-y-auto overflow-x-hidden">
                        <div className="px-6 pt-4 bg-white">
                            {cover && (
                                <img src={cover} alt={eventDetails?.name || 'Event'} className="w-full h-36 lg:h-40 object-cover rounded-2xl" />
                            )}
                        </div>

                        <div className="flex flex-col bg-white gap-4 px-6 py-4">
                            <div>
                                <h3 className="m-0 text-lg lg:text-xl font-extrabold text-[#0f172a] leading-tight break-words">{eventDetails?.name}</h3>
                                {eventDetails?.genre && <p className="m-0 text-sm font-medium text-gray-500">{eventDetails.genre}</p>}
                            </div>
                            <div className="flex items-start gap-2.5">
                                <Calendar className="w-5 h-5 text-gray-400 mt-0.5 shrink-0" />
                                <div className="text-sm min-w-0">
                                    <p className="m-0 font-bold text-[#0f172a]">{formatDateLong(eventDetails?.date)}</p>
                                    {timeRange() && <p className="m-0 font-medium text-gray-500">{timeRange()}</p>}
                                </div>
                            </div>
                            {eventDetails?.venue && (
                                <div className="flex items-start gap-2.5">
                                    <MapPin className="w-5 h-5 text-gray-400 mt-0.5 shrink-0" />
                                    <div className="text-sm min-w-0">
                                        <p className="m-0 font-bold text-[#0f172a] break-words">{eventDetails.venue}</p>
                                        {eventDetails?.venueaddress && <p className="m-0 font-medium text-gray-500 break-words">{eventDetails.venueaddress}</p>}
                                    </div>
                                </div>
                            )}
                            <div className="h-px bg-gray-200" />
                        </div>
                    </div>

                    {step === 2 && (
                        <div className="hidden md:block shrink-0 px-4 md:px-6 pt-3 md:pt-2 pb-1 bg-white">
                            <h4 className="m-0 mb-2 text-base font-extrabold text-[#0f172a]">Coupon Code</h4>
                            <div
                                ref={attachDesktopCouponSlot}
                                className="min-w-0 overflow-x-hidden [&_[class*='overflow-y-auto']]:overflow-x-hidden"
                            />
                        </div>
                    )}

                    <div className="hidden md:block shrink-0 px-6 pt-2 pb-4 bg-white">
                        <h4 className="m-0 text-base font-extrabold text-[#0f172a]">Order Summary</h4>
                        {totalQty === 0 ? (
                            <div className="flex items-center justify-between text-sm mt-2">
                                <span className="flex items-center gap-2 text-gray-500 font-medium">
                                    <ShoppingCart className="w-4 h-4 shrink-0" /> No tickets selected
                                </span>
                                <span className="font-bold text-gray-500 shrink-0">{cur} 0</span>
                            </div>
                        ) : (
                            <div className="flex flex-col gap-2 mt-2">{orderLineItems}</div>
                        )}
                        <div className="h-px bg-gray-200 my-3" />
                        <div className="flex flex-col gap-1.5 text-sm">
                            <div className="flex items-center justify-between gap-3">
                                <span className="text-gray-500 font-medium">Subtotal</span>
                                <span className="font-semibold text-gray-700 shrink-0">{cur} {fmt(fees.subtotal)}</span>
                            </div>
                            <div className="flex items-center justify-between gap-3">
                                <span className="text-gray-500 font-medium">Fees</span>
                                <span className="font-semibold text-gray-700 shrink-0">{cur} {fmt(buyerFees)}</span>
                            </div>
                        </div>
                        <div className="h-px bg-gray-200 my-3" />
                        <div className="flex items-center justify-between gap-3">
                            <span className="text-xl font-extrabold text-[#0f172a]">Total</span>
                            <span className="text-xl font-extrabold text-[#0f172a] shrink-0">{cur} {fmt(fees.total)}</span>
                        </div>

                        <div className="shrink-0 w-full pt-4">
                            {step === 1 ? (
                                <button
                                    type="button"
                                    onClick={goCheckout}
                                    disabled={totalQty === 0 || checkingBlock}
                                    className="flex items-center justify-center gap-2 w-full h-[56px] rounded-2xl bg-[#ffe500] border-none font-extrabold text-black text-lg shadow-[0_4px_12px_rgba(0,0,0,0.08)] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <Lock className="w-5 h-5 shrink-0" />
                                    <span className="truncate">{checkingBlock ? 'Checking…' : checkoutLabel}</span>
                                    <ArrowRight className="w-5 h-5 shrink-0" />
                                </button>
                            ) : Number(fees.total) > 0 ? (
                                <button
                                    type="submit"
                                    form="ticket-checkout-form"
                                    disabled={payNowDisabled}
                                    className="flex items-center justify-center gap-2 w-full h-[56px] rounded-2xl bg-[#ffe500] border-none font-extrabold text-black text-lg shadow-[0_4px_12px_rgba(0,0,0,0.08)] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {payProcessing
                                        ? <span className="w-5 h-5 shrink-0 rounded-full border-2 border-black/30 border-t-black animate-spin" aria-hidden />
                                        : <Lock className="w-5 h-5 shrink-0" />}
                                    <span className="truncate">{payNowDisplay}</span>
                                </button>
                            ) : null}
                            <p className="m-0 mt-2 text-center text-xs font-medium text-gray-400 flex items-center justify-center gap-1.5">
                                <ShieldCheck className="w-3.5 h-3.5 shrink-0" />
                                {step === 1 ? (
                                    <span>Your payment is secure and encrypted</span>
                                ) : (
                                    <span className="inline-flex items-center gap-1.5">Secure checkout powered by {gatewayBadge()}</span>
                                )}
                            </p>
                        </div>
                    </div>

                    {/* Mobile summary. Subtotal and Fees are itemised here exactly as
                        in the desktop Order Summary above — a single all-in Total with
                        an "incl. fees" caption told the buyer fees existed without
                        telling them how much, which is the one number they want to
                        check. The ticket count rides on the Subtotal row so the
                        breakdown costs no extra vertical space in this footer. */}
                    <div className="md:hidden shrink-0 px-4 pt-3">
                        {totalQty > 0 && (
                            <>
                                <div className="flex flex-col gap-1 text-[13px]">
                                    <div className="flex items-center justify-between gap-3">
                                        <span className="text-gray-500 font-medium">
                                            Subtotal
                                            <span className="text-gray-400"> · {totalQty} ticket{totalQty === 1 ? '' : 's'}</span>
                                        </span>
                                        <span className="font-semibold text-gray-700 shrink-0">{cur} {fmt(fees.subtotal)}</span>
                                    </div>
                                    <div className="flex items-center justify-between gap-3">
                                        <span className="text-gray-500 font-medium">Fees</span>
                                        <span className="font-semibold text-gray-700 shrink-0">{cur} {fmt(buyerFees)}</span>
                                    </div>
                                </div>
                                <div className="h-px bg-gray-200 my-2" />
                            </>
                        )}
                        <div className="flex items-center justify-between gap-3">
                            <h4 className="m-0 text-sm font-extrabold text-[#0f172a]">Total</h4>
                            <span className="text-lg font-extrabold text-[#0f172a] shrink-0">{cur} {fmt(fees.total)}</span>
                        </div>
                    </div>

                    <div className="md:hidden shrink-0 px-4 pt-2 pb-[max(0.75rem,env(safe-area-inset-bottom))] bg-[#ffff]">
                        {step === 1 ? (
                            <button
                                type="button"
                                onClick={goCheckout}
                                disabled={totalQty === 0 || checkingBlock}
                                className="flex items-center justify-center gap-2 w-full h-[52px] md:h-[56px] rounded-2xl bg-[#ffe500] border-none font-extrabold text-black text-base md:text-lg shadow-[0_4px_12px_rgba(0,0,0,0.08)] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <Lock className="w-4 h-4 md:w-5 md:h-5 shrink-0" />
                                <span className="truncate">{checkingBlock ? 'Checking…' : checkoutLabel}</span>
                                <ArrowRight className="w-4 h-4 md:w-5 md:h-5 shrink-0" />
                            </button>
                        ) : Number(fees.total) > 0 ? (
                            <button
                                type="submit"
                                form="ticket-checkout-form"
                                disabled={payNowDisabled}
                                className="flex items-center justify-center gap-2 w-full h-[52px] md:h-[56px] rounded-2xl bg-[#ffe500] border-none font-extrabold text-black text-base md:text-lg shadow-[0_4px_12px_rgba(0,0,0,0.08)] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {payProcessing
                                    ? <span className="w-4 h-4 md:w-5 md:h-5 shrink-0 rounded-full border-2 border-black/30 border-t-black animate-spin" aria-hidden />
                                    : <Lock className="w-4 h-4 md:w-5 md:h-5 shrink-0" />}
                                <span className="truncate">{payNowDisplay}</span>
                            </button>
                        ) : null}
                        {step === 2 && (
                            <p className="m-0 mt-2 text-center text-[11px] font-medium text-gray-400 flex items-center justify-center gap-1.5">
                                <ShieldCheck className="w-3.5 h-3.5 shrink-0" />
                                <span className="inline-flex items-center gap-1.5">Secure checkout powered by {gatewayBadge()}</span>
                            </p>
                        )}
                    </div>
                </div>
                </>
                )}
            </div>
        </div>
    );

    return createPortal(modal, document.body);
}
